<template>
  <div class="map-container">
    <gmap-map
      ref="gmapmap"
      :center="center"
      :zoom="zoom"
      map-type-id="roadmap"
      :options="mapOptions"
      @zoom_changed="zoomed"
      @dragstart="disableDestinSelect"
      @idle="enableDestinSelect"
    >
      <div
        v-for="data in destins"
        :key="getDestinsKey(data.code)"
      >
        <gmap-custom-marker
          v-if="parseCoordinates(data) !== null"
          :marker="parseCoordinates(data)"
          alignment="center"
          class="circle"
          :class="{
            connection: notDirect(data),
            active: isItemActive(data),
            destin: isActive(data.code, selectedDestin),
            'via-point': data.viaPoint
          }"
          @click.native="selectItem(data)"
          @touchend.native="selectItem(data)"
        />

        <gmap-custom-marker
          v-if="parseCoordinates(data) !== null"
          :marker="parseCoordinates(data)"
          alignment="top"
          class="label-block"
          :class="{
            active: isItemActive(data),
            origin: isActive(data.code, selectedOrigin),
            destin: isActive(data.code, selectedDestin),
            connection: notDirect(data)
          }"
          :offset-y="getYOffset(data)"
        >
          <custom-label
            :direction="isActive(data.code, selectedOrigin) ? 'origin' : 'destin'"
            :active="isItemActive(data)"
            :city="data.city"
            :code="data.code"
            @close="closeItem(data)"
          />
        </gmap-custom-marker>
      </div>

      <gmap-polyline
        v-if="curvedLine.length && selectedDestin !== null"
        class="polyline"
        :path="curvedLine"
        :options="polylineOptions"
      />
    </gmap-map>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import { gmapApi } from 'gmap-vue'
  import GmapCustomMarker from 'vue2-gmap-custom-marker'
  import Label from 'containers/Pages/Map/Label'
  import mapStyle from 'containers/Pages/Map/mapStyle'

  export default {
    components: {
      GmapCustomMarker,
      'custom-label': Label
    },
    data() {
      return {
        zoom: 3,
        icon: null,
        conIcon: null,
        rixCode: 'RIX',
        rixCoordinates: {
          lat: 56.92359924316406,
          lng: 23.971099853515625
        },
        defaultCenter: { lat: 52.1656990051, lng: 20.967100143399996 },
        styles: mapStyle,
        unsubscribeAction: () => null,
        mapOptions: {
          styles: this.styles,
          minZoom: 3,
          maxZoom: 5,
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false
        },
        polylineOptions: {
          strokeColor: '#919c0a',
          strokeOpacity: 0,
          strokeWeight: 2,
          geodesic: true,
          icons: [
            {
              icon: {
                path: 'M 0,-1 0,1',
                strokeOpacity: 1,
                scale: 3
              },
              offset: '0',
              repeat: '15px'
            }
          ],
          zIndex: 150
        },
        canSelectDestin: true
      }
    },
    computed: {
      ...mapState({
        airport: (state) => state.destinationsSilo.airport,
        selectedDestin: (state) => state.destinationsSilo.selectedDestination,
        countryFromUrl: (state) => state.destinationsSilo.countryFromUrl
      }),
      google: gmapApi,
      destinations() {
        return this.$store.getters['destinationsSilo/destinations'] || []
      },
      selectedOrigin() {
        return {
          ...this.airport,
          path: {
            lat: this.airport.latitude,
            lng: this.airport.longitude
          }
        }
      },
      filteredDestinations() {
        if (this.countryFromUrl === '') {
          return this.destinations
        }

        return this.destinations.filter(
          ({ code, countryCode }) =>
            code.slice(0, 3).toUpperCase() === this.airport?.code ||
            countryCode === this.countryFromUrl
        )
      },
      center() {
        if (!this.selectedOrigin?.code) {
          this.defaultCenter
        }

        return this.parseCoordinates(this.selectedOrigin, true)
      },
      destins() {
        const filtered = this.filteredDestinations.filter((item) => {
          const lng = item.lng || item.longitude
          const lat = item.lat || item.latitute

          const hasCoords = lng !== 0 && lat !== 0

          return hasCoords || item.code === `${this.rixCode}A`.toLowerCase()
        })

        if (filtered.length === 2) {
          const onlyDestin = filtered.find((item) => {
            const code = item.code?.slice(0, 3).toUpperCase()
            return code !== this.selectedOrigin?.code
          })
          if (!onlyDestin.manuallyClosed) {
            this.selectItem(onlyDestin)
          }
        }
        return filtered
      },
      curvedLine() {
        if (!this.selectedOrigin?.code || !this.selectedDestin?.code || !this.google) {
          return []
        }

        const originData = this.selectedOrigin?.path || {}
        const destinData = this.selectedDestin?.path || {}
        const origin = this.getLatLng(originData, true)
        const destin = this.getLatLng(destinData, true)
        const rix = this.getLatLng(this.rixCoordinates)

        const isHome = [this.selectedDestin.code, this.selectedOrigin.code].includes(
          this.rixCode
        )

        return this.selectedDestin.direct || isHome
          ? [origin, destin]
          : [origin, rix, destin]
      }
    },
    beforeUpdate() {
      if (this.selectedDestin !== null) {
        return
      }

      if (this.icon) {
        this.icon.setMap(null)
        this.icon = null
      }

      if (this.conIcon) {
        this.conIcon.setMap(null)
        this.conIcon = null
      }
    },
    mounted() {
      this.unsubscribeAction = this.$store.subscribeAction(({ type }) => {
        if (type !== 'destinationsSilo/clearFilters') {
          return
        }

        this.zoom = 3
      })

      this.$store.commit('destinationsSilo/withOrigin', true)
    },
    beforeDestroy() {
      this.unsubscribeAction()
    },
    methods: {
      enableDestinSelect() {
        setTimeout(() => {
          this.canSelectDestin = true
        }, 1)
      },
      disableDestinSelect() {
        this.canSelectDestin = false
      },
      getLatLng(coords, parse = false) {
        const coordinates = parse ? this.parseCoordinates(coords) : coords

        return new this.google.maps.LatLng(coordinates)
      },
      notDirect(data) {
        if (
          !this.selectedDestin?.code ||
          this.selectedDestin.direct ||
          !this.selectedOrigin?.code
        ) {
          return false
        }

        const isOriginNotRix = this.selectedOrigin.code !== this.rixCode

        return data.code?.slice(0, 3).toUpperCase() === this.rixCode && isOriginNotRix
      },
      getYOffset(data) {
        const isDestination = this.isActive(data.code, this.selectedDestin)

        if (isDestination) {
          return -22
        }

        if (this.notDirect(data)) {
          return -18
        }

        return this.isItemActive(data) ? -20 : -26
      },
      zoomed(data) {
        this.disableDestinSelect()
        this.zoom = data
      },
      isItemActive(data) {
        return (
          this.isActive(data.code, this.selectedOrigin) ||
          this.isActive(data.code, this.selectedDestin)
        )
      },
      parseCoordinates(data, longKey) {
        const { latKey, lngKey } = longKey
          ? { latKey: 'latitude', lngKey: 'longitude' }
          : { latKey: 'lat', lngKey: 'lng' }

        const lat = parseFloat(data[latKey])
        const lng = parseFloat(data[lngKey])

        if (Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) {
          return this.defaultCenter
        }

        return { lng, lat }
      },
      isActive(code, selected) {
        if (!selected) {
          return false
        }

        return (
          code?.slice(0, 3).toUpperCase() === selected?.code?.slice(0, 3).toUpperCase()
        )
      },
      closeItem(item) {
        item.manuallyClosed = true
        this.$store.commit('destinationsSilo/selectDestination', null)
        this.zoom = 3

        if (this.icon) {
          this.icon.setMap(null)
          this.icon = null
        }

        if (this.conIcon) {
          this.conIcon.setMap(null)
          this.conIcon = null
        }

        this.$refs.gmapmap.$mapPromise.then((map) => {
          map.panTo(this.center)
        })
      },
      fitToBounds(path) {
        this.$refs.gmapmap.$mapPromise.then((map) => {
          const se = this.getLatLng(this.selectedOrigin.path, true)
          const nw = this.getLatLng(path)

          const bounds =
            se.lng() > nw.lng() ? this.getBounds(nw, se) : this.getBounds(se, nw)

          map.fitBounds(bounds, 100)
        })
      },
      selectItem(data) {
        if (!this.canSelectDestin) return
        if (data.viaPoint) {
          return
        }

        const code = data.code?.slice(0, 3).toUpperCase()
        const path = this.parseCoordinates(data)

        if (code === this.selectedOrigin?.code) {
          return
        }

        if (!this.selectedDestin?.code || this.selectedDestin.code !== code) {
          this.$store.commit('destinationsSilo/selectDestination', {
            code,
            path,
            direct: data.isDirect
          })

          if (this.icon) {
            this.icon.setMap(null)
            this.icon = null
          }

          if (this.conIcon) {
            this.conIcon.setMap(null)
            this.conIcon = null
          }
        }

        this.fitToBounds(path)
      },
      getBounds(se, nw) {
        return new this.google.maps.LatLngBounds(se, nw)
      },
      getDestinsKey(code) {
        const active = this.isActive(code, this.selectedOrigin) ? '-force-refresh' : 0

        return `${code?.slice(0, 3).toUpperCase()}${active}`
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';
  @import '~assets/styles/components/route-map.scss';

  /deep/ .vue-map-container {
    height: 86vh !important;

    @include bp-mf-tablet {
      height: 750px !important;
    }
  }

  /deep/ .vue-map {
    height: 100% !important;
  }
</style>
